import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Customer } from 'src/app/modules/shared/entities/customer.class';
import { AppInfoService } from 'core/app-info/app-info.service';
import { AppLocalization } from 'src/app/modules/shared/entities/app-localization';
import { DeviceDetectorService } from 'ngx-device-detector';
import { QuoteDetailsService } from 'src/app/modules/pos/quotes/services/quote-details.service';
import { QuoteTemplateValues } from 'src/app/modules/shared/entities/quote-template-values.class';
import { LastQuotesFilters } from '../../../entities/last-quotes-filters.class';
import { LastQuotesFiltersComponent } from './last-quotes-filters/last-quotes-filters.component';

@Component({
  selector: 'app-last-quotes-search',
  templateUrl: './last-quotes-search.component.html',
  styleUrls: ['./last-quotes-search.component.scss'],
  standalone: false
})
export class LastQuotesSearchComponent implements OnInit {

  @Input() customer: Customer = null;
  @Input() isFromDashboard: boolean;
  @Input() isCustomerDashboard: boolean;
  @Input() filters: LastQuotesFilters;
  @Output() filterChanged = new EventEmitter<LastQuotesFilters>();

  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

  displayedSearchValue = new UntypedFormControl('');
  appStrings: AppLocalization;
  filterData: LastQuotesFilters = null;
  currentSearchText = '';
  isOnline = true;
  isMobile = false;
  confidenceOptions: { key: string, label: string }[] = [];

  constructor(
    private dialog: MatDialog,
    private appInfoService: AppInfoService,
    private deviceService: DeviceDetectorService,
    private quoteDetailsService: QuoteDetailsService
  ) { }

  ngOnInit(): void {
    this.isMobile = this.deviceService.isMobile();
    this.appStrings = this.appInfoService.appStrings;
    this.isOnline = this.appInfoService.state.isOnline;

    this.quoteDetailsService.getStaticValues(([templateValues]: QuoteTemplateValues[]) => {
      if (templateValues.ConfidenceToClose) {
        this.confidenceOptions = Object.keys(templateValues.ConfidenceToClose).map(key => ({
          key, label: templateValues.ConfidenceToClose[key]
        }));
      }
    });

    if (this.filters) {
      this.handleFilterChange(this.filters);
    }
  }

  openAdvancedFilteringDialog() {
    const advancedFilteringDialog = this.dialog.open(LastQuotesFiltersComponent, {
      data: {
        filters: this.filterData,
        customer: this.customer,
        isCustomerDashboard: this.isCustomerDashboard,
        isDs: this.isFromDashboard
      },
      panelClass: [...(this.isFromDashboard ? ['ds'] : []), 'fullWidth', this.deviceService.isMobile() ? 'fullScreen' : ''],
    });

    advancedFilteringDialog.afterClosed().subscribe((values: LastQuotesFilters) => this.handleFilterChange(values));
  }

  handleFilterChange(filterValues: LastQuotesFilters) {
    if (!filterValues) {
      return;
    }

    if (filterValues.clearAllFilters) {
      this.displayedSearchValue.setValue('');
      this.currentSearchText = this.displayedSearchValue.value;
      this.filterChanged.emit(filterValues);
      this.filterData = null;
      return;
    }

    this.filterData = filterValues;
    const displayedFilters = Object.assign({}, this.filterData);

    // Map confidence value to label for display
    const confidenceLabel = displayedFilters.confidence
      ? this.confidenceOptions.find(opt => opt.key === displayedFilters.confidence)?.label
      : '';

    this.displayedSearchValue.setValue(
      Object.keys(filterValues)
        .filter(key => key !== 'startDate' && key !== 'endDate' && key !== 'confidence')
        .map(key =>
          displayedFilters[key]
        )
        .concat(confidenceLabel ? [confidenceLabel] : [])
        .filter(Boolean)
        .join('; ')
    );
    this.currentSearchText = this.displayedSearchValue.value;
    this.filterChanged.emit(filterValues);
  }

  /**
   * Clear all filters from the main form
   */
  clearFilters() {
    this.currentSearchText = '';
    this.filterData = null;
    this.displayedSearchValue.setValue('');

    this.filterChanged.emit(this.filterData);
  }
}
